"use client";

/**
 * WelcomeModal — Multi-step welcome tour shown once after signup.
 *
 * 3 steps:
 *   1. Intro       — what Velyq is, one-sentence pitch
 *   2. Features    — 4-card grid of the main tools
 *   3. Goal picker — saved to user_profiles.onboarding_goal, used by the
 *                    dashboard to surface the most relevant CTA
 *
 * Goals (backend enum):
 *  - first_job           → premier emploi / alternance / stage
 *  - career_change       → reconversion de secteur
 *  - actively_searching  → recherche active (10+ candidatures/mois)
 *  - negotiating         → offre en main, préparation négociation
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Briefcase, Check, Compass, FileText, GraduationCap, Handshake, Mic, MessageSquare, Search, Sparkles } from "lucide-react";
import { useAuthStore } from "@/lib/store";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { users as usersApi } from "@/lib/api";
export type OnboardingGoal = "first_job" | "career_change" | "actively_searching" | "negotiating";
interface Goal {
  id: OnboardingGoal;
  icon: typeof Sparkles;
  tone: string;
  iconColor: string;
  titleFr: string;
  titleEn: string;
  descFr: string;
  descEn: string;
}
const GOALS: Goal[] = [{
  id: "first_job",
  icon: GraduationCap,
  tone: "from-[#5278ff]/20 to-[#5278ff]/5",
  iconColor: "text-[#818cf8]",
  titleFr: "Mon premier emploi",
  titleEn: "My first job",
  descFr: "Stage, alternance ou premier CDI — je débute.",
  descEn: "Internship, apprenticeship or first full-time role."
}, {
  id: "career_change",
  icon: Compass,
  tone: "from-[#a855f7]/20 to-[#a855f7]/5",
  iconColor: "text-primary",
  titleFr: "Je change de secteur",
  titleEn: "I'm changing fields",
  descFr: "Je pivote de métier ou de domaine — reconversion.",
  descEn: "Switching industries or roles — career pivot."
}, {
  id: "actively_searching",
  icon: Search,
  tone: "from-[#c084fc]/20 to-[#c084fc]/5",
  iconColor: "text-[#c084fc]",
  titleFr: "Je cherche activement",
  titleEn: "I'm actively searching",
  descFr: "Je postule à plusieurs offres en parallèle.",
  descEn: "Applying to multiple roles in parallel."
}, {
  id: "negotiating",
  icon: Handshake,
  tone: "from-[#d946ef]/20 to-[#d946ef]/5",
  iconColor: "text-[#d946ef]",
  titleFr: "Je négocie une offre",
  titleEn: "I'm negotiating an offer",
  descFr: "J'ai une proposition en main — je veux la maximiser.",
  descEn: "I've got an offer — time to maximize it."
}];
interface Feature {
  icon: typeof MessageSquare;
  iconColor: string;
  titleFr: string;
  titleEn: string;
  descFr: string;
  descEn: string;
}
const FEATURES: Feature[] = [{
  icon: MessageSquare,
  iconColor: "text-[#818cf8]",
  titleFr: "Entretiens simulés",
  titleEn: "Mock interviews",
  descFr: "Questions générées par IA, feedback immédiat, mode vocal live.",
  descEn: "AI-generated questions, instant feedback, live voice mode."
}, {
  icon: FileText,
  iconColor: "text-primary",
  titleFr: "Analyse ATS",
  titleEn: "ATS analysis",
  descFr: "Score CV/offre, mots-clés manquants, suggestions concrètes.",
  descEn: "CV-vs-offer score, missing keywords, actionable fixes."
}, {
  icon: Briefcase,
  iconColor: "text-[#c084fc]",
  titleFr: "Tracker de candidatures",
  titleEn: "Application tracker",
  descFr: "Pipeline visuel, relances, lettres de motivation IA.",
  descEn: "Visual pipeline, follow-ups, AI cover letters."
}, {
  icon: Mic,
  iconColor: "text-[#d946ef]",
  titleFr: "Training Hub",
  titleEn: "Training Hub",
  descFr: "Entraîne-toi sur des cas concrets — cold call, pitch, négo.",
  descEn: "Practice real scenarios — cold calls, pitches, negotiation."
}];
type Step = 0 | 1 | 2;
export function WelcomeModal() {
  const {
    accessToken,
    user
  } = useAuthStore();
  const {
    locale
  } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<Step>(0);
  const [selected, setSelected] = useState<OnboardingGoal | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Defer this modal until the user has accepted the ToS — otherwise both
  // dialogs render at once, Radix's focus-trap stays on the ToS modal, and
  // the welcome's Next button can't be clicked. ToSUpdateModal sets
  // user.tos_accepted_at on accept, which re-triggers this effect.
  const tosAccepted = Boolean(user?.tos_accepted_at);
  useEffect(() => {
    if (!accessToken || !tosAccepted) return;
    const checkWelcome = async () => {
      try {
        const data = await usersApi.onboarding(accessToken);
        if (!data.welcome_seen) setIsOpen(true);
      } catch (error) {
        console.error("Failed to check welcome status:", error);
      }
    };
    checkWelcome();
  }, [accessToken, tosAccepted]);
  const notifyClosed = () => {
    // Tell DashboardTour the welcome flow is done so it can start.
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("velyq:welcome-closed"));
    }
  };
  const handleSubmit = async () => {
    if (!selected || submitting) return;
    setSubmitting(true);
    try {
      await usersApi.markWelcomeSeen(accessToken!, selected);
    } catch (error_0) {
      console.error("Failed to save onboarding goal:", error_0);
    } finally {
      setSubmitting(false);
      setIsOpen(false);
      notifyClosed();
    }
  };
  const handleSkip = async () => {
    setSubmitting(true);
    try {
      await usersApi.markWelcomeSeen(accessToken!);
    } catch (error_1) {
      console.error("Failed to mark welcome seen:", error_1);
    } finally {
      setSubmitting(false);
      setIsOpen(false);
      notifyClosed();
    }
  };
  const firstName = user?.first_name || (locale === "fr" ? "à bord" : "aboard");
  const isLastStep = step === 2;
  const canAdvance = !isLastStep || selected !== null;
  return <Dialog open={isOpen} onOpenChange={open => {
    if (!open) return;
    setIsOpen(open);
  }}>
      <DialogContent showCloseButton={false} className="max-h-[90vh] overflow-y-auto p-0 sm:max-w-2xl">
        {/* Header */}
        <div className="relative overflow-hidden rounded-t-lg border-b border-border/30 px-6 pb-5 pt-7 sm:px-8 sm:pt-9">
          <div className="pointer-events-none absolute -top-20 left-1/2 h-[240px] w-[320px] -translate-x-1/2 rounded-full bg-primary/[0.25] blur-[80px]" />
          <div className="pointer-events-none absolute -right-10 -bottom-10 h-[180px] w-[180px] rounded-full bg-[#5278ff]/[0.15] blur-[60px]" />

          <DialogHeader className="relative space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-primary">
                <Sparkles className="h-3 w-3" />
                {locale === "fr" ? "Bienvenue" : "Welcome"}
              </span>
            </div>

            {step === 0 && <>
                <DialogTitle className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
                  {locale === "fr" ? <>
                      Salut {firstName}, bienvenue chez{" "}
                      <span className="bg-gradient-to-br from-primary via-[#c084fc] to-[#5278ff] bg-clip-text text-transparent">
                        Velyq
                      </span>
                    </> : <>
                      Hey {firstName}, welcome to{" "}
                      <span className="bg-gradient-to-br from-primary via-[#c084fc] to-[#5278ff] bg-clip-text text-transparent">
                        Velyq
                      </span>
                    </>}
                </DialogTitle>
                <DialogDescription className="text-[13px] leading-relaxed">
                  {locale === "fr" ? "Velyq est ton coach IA pour décrocher l'entretien — entretiens simulés, analyse ATS de ton CV, tracker de candidatures et entraînement vocal. On va te montrer comment ça marche." : "Velyq is your AI coach for landing the interview — mock interviews, ATS resume analysis, application tracker, and live voice practice. Let's show you around."}
                </DialogDescription>
              </>}

            {step === 1 && <>
                <DialogTitle className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
                  {locale === "fr" ? <>
                      Voici ce qu&apos;on{" "}
                      <span className="bg-gradient-to-br from-primary via-[#c084fc] to-[#5278ff] bg-clip-text text-transparent">
                        peut faire
                      </span>{" "}
                      ensemble
                    </> : <>
                      Here&apos;s what we can{" "}
                      <span className="bg-gradient-to-br from-primary via-[#c084fc] to-[#5278ff] bg-clip-text text-transparent">
                        do together
                      </span>
                    </>}
                </DialogTitle>
                <DialogDescription className="text-[13px] leading-relaxed">
                  {locale === "fr" ? "Quatre outils pour couvrir toute ta préparation, du CV jusqu'à la négociation de l'offre." : "Four tools that cover your full prep, from resume to offer negotiation."}
                </DialogDescription>
              </>}

            {step === 2 && <>
                <DialogTitle className="font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl">
                  {locale === "fr" ? <>
                      Qu&apos;est-ce qui{" "}
                      <span className="bg-gradient-to-br from-primary via-[#c084fc] to-[#5278ff] bg-clip-text text-transparent">
                        t&apos;amène
                      </span>{" "}
                      chez Velyq ?
                    </> : <>
                      What brings{" "}
                      <span className="bg-gradient-to-br from-primary via-[#c084fc] to-[#5278ff] bg-clip-text text-transparent">
                        you here
                      </span>
                      ?
                    </>}
                </DialogTitle>
                <DialogDescription className="text-[13px] leading-relaxed">
                  {locale === "fr" ? "Ta réponse guide ton dashboard et les outils qu'on te suggère en premier. Tu pourras changer à tout moment." : "Your answer shapes your dashboard and the tools we surface first. You can change it anytime."}
                </DialogDescription>
              </>}
          </DialogHeader>
        </div>

        {/* Body — content per step */}
        <div className="px-6 pt-5 pb-3 sm:px-8">
          {step === 0 && <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/25 to-[#5278ff]/15 shadow-[0_0_48px_-8px_rgba(168,85,247,0.5)]">
                <Sparkles className="h-10 w-10 text-primary" strokeWidth={1.5} />
              </div>
              <p className="max-w-md text-[13px] leading-relaxed text-muted-foreground">
                {locale === "fr" ? "Tout est conçu pour t'aider à transformer chaque candidature en entretien, et chaque entretien en offre. Prêt à découvrir ?" : "Everything is built to turn each application into an interview, and each interview into an offer. Ready to dive in?"}
              </p>
            </div>}

          {step === 1 && <div className="grid gap-2.5 sm:grid-cols-2">
              {FEATURES.map(feature => {
            const title = locale === "fr" ? feature.titleFr : feature.titleEn;
            const desc = locale === "fr" ? feature.descFr : feature.descEn;
            return <div key={title} className="relative overflow-hidden rounded-xl border border-border/50 bg-card/50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-card/70">
                        <feature.icon className={`h-5 w-5 ${feature.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[14px] font-semibold leading-tight text-foreground">
                          {title}
                        </p>
                        <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                          {desc}
                        </p>
                      </div>
                    </div>
                  </div>;
          })}
            </div>}

          {step === 2 && <div className="grid gap-2.5 sm:grid-cols-2">
              {GOALS.map(goal => {
            const isSelected = selected === goal.id;
            const title_0 = locale === "fr" ? goal.titleFr : goal.titleEn;
            const desc_0 = locale === "fr" ? goal.descFr : goal.descEn;
            return <button key={goal.id} type="button" onClick={() => setSelected(goal.id)} className={`group relative cursor-pointer overflow-hidden rounded-xl border p-4 text-left transition-all ${isSelected ? "border-primary/60 bg-gradient-to-br from-primary/[0.12] to-primary/[0.03] shadow-[0_0_32px_-12px_rgba(168,85,247,0.5)]" : "border-border/50 bg-card/50 hover:border-primary/30 hover:bg-card/70"}`}>
                    <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${goal.tone} ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-60"} transition-opacity`} />
                    <div className="relative flex items-start gap-3">
                      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-colors ${isSelected ? "border-primary/40 bg-primary/[0.14]" : "border-border/50 bg-card/70 group-hover:border-primary/25"}`}>
                        <goal.icon className={`h-5 w-5 ${goal.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-[14px] font-semibold leading-tight text-foreground">
                            {title_0}
                          </p>
                          {isSelected && <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary">
                              <Check className="h-3 w-3 text-white" strokeWidth={3} />
                            </div>}
                        </div>
                        <p className="mt-1 text-[12px] leading-relaxed text-muted-foreground">
                          {desc_0}
                        </p>
                      </div>
                    </div>
                  </button>;
          })}
            </div>}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-border/30 px-6 py-4 sm:px-8">
          <button type="button" onClick={handleSkip} disabled={submitting} className="text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50">
            {locale === "fr" ? "Passer pour l'instant" : "Skip for now"}
          </button>

          {/* Step dots */}
          <div className="flex items-center gap-1.5">
            {[0, 1, 2].map(i => <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-primary" : "w-1.5 bg-border"}`} />)}
          </div>

          <div className="flex items-center gap-2">
            {step > 0 && <Button type="button" onClick={() => setStep(s => s - 1 as Step)} disabled={submitting} size="sm" variant="ghost" className="gap-1.5">
                <ArrowLeft className="h-3.5 w-3.5" />
                {locale === "fr" ? "Retour" : "Back"}
              </Button>}
            <Button onClick={() => isLastStep ? handleSubmit() : setStep(s_0 => s_0 + 1 as Step)} disabled={!canAdvance || submitting} size="sm" className="gap-1.5">
              {submitting ? locale === "fr" ? "Enregistrement…" : "Saving…" : isLastStep ? locale === "fr" ? "Continuer" : "Continue" : locale === "fr" ? "Suivant" : "Next"}
              {!submitting && <ArrowRight className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}